import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketplaceListingService, reviewService } from '../services/api';

interface Card {
  id: string;
  name: string;
  imageUrl?: string;
  cardNumber: string;
  rarity?: string;
  type?: string;
}

interface MarketplaceListing {
  id: string;
  isBundle?: boolean;
  bundleName?: string;
  bundleItems?: any;
  customCardName?: string;
  price: number;
  condition: string;
  quantity: number;
  description?: string;
  imageUrls: string;
  status: string;
  listingType: 'WTS' | 'WTB';
  card?: Card;
  seller: {
    id: number;
    displayName: string;
  };
  createdAt: string;
}

const cardTypes = ['Avatar', 'Magic', 'Land', 'Construct', 'Life'];

const NewMarketplacePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [listings, setListings] = useState<MarketplaceListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [listingTypeFilter, setListingTypeFilter] = useState<'all' | 'WTS' | 'WTB'>('all');
  const [filterRarity, setFilterRarity] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  // const [bundleFilter, setBundleFilter] = useState<'all' | 'single' | 'bundle'>('all'); // Removed
  const [userRatings, setUserRatings] = useState<Record<number, { averageRating: number; totalReviews: number }>>({});

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchListings();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, sortBy, sortOrder, listingTypeFilter, filterRarity, filterType, minPrice, maxPrice]);

  /* 
   * Removed manual fetchListings call on filter changes since they are now included in the debounced effect.
   * This prevents double-fetching and ensures consistent state.
   */

  const fetchListings = async () => {
    try {
      setLoading(true);
      const params: any = { sortBy, sortOrder };
      if (minPrice) params.minPrice = minPrice;
      if (maxPrice) params.maxPrice = maxPrice;
      if (filterRarity !== 'all') params.rarity = filterRarity;
      if (filterType !== 'all') params.type = filterType;
      if (searchTerm) params.search = searchTerm;
      if (listingTypeFilter !== 'all') params.listingType = listingTypeFilter;
      // Removed bundle filter logic as requested by user
      // if (bundleFilter === 'bundle') params.isBundle = true;
      // if (bundleFilter === 'single') params.isBundle = false;

      const response = await marketplaceListingService.getListings(params);
      setListings(response.data.data);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch marketplace listings:', error);
      setError('Failed to load marketplace listings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const sellerIds = Array.from(new Set(listings.map((l) => l.seller.id)));
    const idsToFetch = sellerIds.filter((id) => userRatings[id] === undefined);
    if (idsToFetch.length === 0) return;

    const fetchRatings = async () => {
      const entries = await Promise.all(
        idsToFetch.map(async (id) => {
          try {
            const res = await reviewService.getUserReviews(String(id), { page: 1, limit: 1 });
            const summary = res.data.data.summary;
            return [id, { averageRating: summary?.averageRating || 0, totalReviews: summary?.totalReviews || 0 }] as const;
          } catch (err) {
            console.error('Failed to fetch rating for user', id, err);
            return [id, { averageRating: 0, totalReviews: 0 }] as const;
          }
        })
      );
      setUserRatings((prev) => {
        const next = { ...prev };
        entries.forEach(([id, rating]) => {
          next[id] = rating;
        });
        return next;
      });
    };

    fetchRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings]);

  const getRarityColor = (rarity: string) => {
    const rarityLower = rarity.toLowerCase();
    if (rarityLower === 'c' || rarityLower === 'common') return 'bg-gray-500 text-white';
    if (rarityLower === 'r' || rarityLower === 'rare') return 'bg-blue-500 text-white';
    if (rarityLower === 'sr' || rarityLower === 'super rare') return 'bg-purple-500 text-white';
    if (rarityLower === 'ur' || rarityLower === 'ultra rare') return 'bg-yellow-500 text-white';
    if (rarityLower === 'scr' || rarityLower === 'secret rare') {
      return 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 text-white';
    }
    if (rarityLower === 'cbr') return 'bg-[#D2B48C] text-black';
    if (rarityLower === 'pr') return 'bg-[#F5F5F5] text-black border border-gray-300';
    return 'bg-gray-500 text-white';
  };

  const getRarityAbbreviation = (rarity: string) => {
    const rarityLower = rarity.toLowerCase();
    if (rarityLower === 'c' || rarityLower === 'common') return 'C';
    if (rarityLower === 'r' || rarityLower === 'rare') return 'R';
    if (rarityLower === 'sr' || rarityLower === 'super rare') return 'SR';
    if (rarityLower === 'ur' || rarityLower === 'ultra rare') return 'UR';
    if (rarityLower === 'scr' || rarityLower === 'secret rare') return 'SCR';
    if (rarityLower === 'cbr') return 'CBR';
    if (rarityLower === 'pr') return 'PR';
    return rarity.toUpperCase();
  };

  /* 
   * Rarity Options Mapping
   * Maps display labels to database values (abbreviations).
   * Note: 'USEC' found in DB is ignored as per user request.
   */
  const rarityOptions = [
    { label: 'Common', value: 'C' },
    { label: 'Rare', value: 'R' },
    { label: 'Super Rare', value: 'SR' },
    { label: 'Ultra Rare', value: 'UR' },
    { label: 'SCR', value: 'SCR' },
    { label: 'CBR', value: 'CBR' },
    { label: 'PR', value: 'PR' }
  ];

  const getTypeBackground = (type: string) => {
    switch (type) {
      case 'Avatar': return 'bg-red-500 text-white';
      case 'Magic': return 'bg-blue-500 text-white';
      case 'Land': return 'bg-green-500 text-white';
      case 'Construct': return 'bg-yellow-500 text-white';
      case 'Life': return 'bg-gray-500 text-white';
      default: return 'bg-gray-100 text-gray-700';
    }
  };


  const parseImages = (imageUrls: string): string[] => {
    try {
      return JSON.parse(imageUrls || '[]');
    } catch {
      return [];
    }
  };

  const parseBundleItems = (raw: any): { name: string; quantity?: number; notes?: string }[] => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= rating ? 'text-amber-500' : 'text-gray-300'}>
          ★
        </span>
      ))}
    </div>
  );

  // Removed global loading check to keep UI visible


  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold mb-2">{t('marketplace.title')}</h1>
          <p className="text-gray-600">
            {t('marketplace.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/marketplace/sell?type=WTB')}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition font-medium"
          >
            + {t('marketplace.buy')}
          </button>
          <button
            onClick={() => navigate('/marketplace/sell?type=WTS')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            + {t('marketplace.sell')}
          </button>
        </div>
      </div>

      {/* WTS/WTB Tabs */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setListingTypeFilter('all')}
          className={`px-6 py-3 rounded-lg font-medium transition ${listingTypeFilter === 'all'
            ? 'bg-gray-800 text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          {t('marketplace.allListings')}
        </button>
        <button
          onClick={() => setListingTypeFilter('WTS')}
          className={`px-6 py-3 rounded-lg font-medium transition ${listingTypeFilter === 'WTS'
            ? 'bg-blue-600 text-white'
            : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
        >
          {t('marketplace.wts')}
        </button>
        <button
          onClick={() => setListingTypeFilter('WTB')}
          className={`px-6 py-3 rounded-lg font-medium transition ${listingTypeFilter === 'WTB'
            ? 'bg-green-600 text-white'
            : 'bg-green-50 text-green-700 hover:bg-green-100'
            }`}
        >
          {t('marketplace.wtb')}
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="mb-8 bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold">{t('marketplace.searchFilters')}</h2>
            <p className="text-sm text-gray-500">{t('marketplace.refineListings')}</p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            // fetchListings(); // Submit handler no longer needed for auto-search
          }}
          className="space-y-4"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-2">
            {/* Search */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('marketplace.searchCards')}
              </label>
              <input
                type="text"
                placeholder={t('marketplace.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2 sm:gap-2 sm:self-end">
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t('browse.searchButton')}
              </button>

              <button
                type="button"
                onClick={() => setFiltersOpen((open) => !open)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                aria-expanded={filtersOpen}
              >
                {filtersOpen ? t('browse.hideFilters') : t('browse.showFilters')}
              </button>
            </div>
          </div>

          {filtersOpen && (
            <>
              <div className="grid md:grid-cols-4 gap-4">
                {/* Rarity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('marketplace.rarity')}
                  </label>
                  <select
                    value={filterRarity}
                    onChange={(e) => setFilterRarity(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">{t('marketplace.allRarities')}</option>
                    {rarityOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('marketplace.type')}
                  </label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">{t('marketplace.allTypes')}</option>
                    {cardTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('marketplace.minPrice')}
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('marketplace.maxPrice')}
                  </label>
                  <input
                    type="number"
                    placeholder="1000"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Sort */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('marketplace.sortBy')}
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="createdAt">{t('marketplace.newest')}</option>
                    <option value="price">{t('marketplace.price')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('marketplace.order')}
                  </label>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="DESC">{t('marketplace.descending')}</option>
                    <option value="ASC">{t('marketplace.ascending')}</option>
                  </select>
                </div>
              </div>

              {/* Removed Listing Content Filter */}
              {/* 
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('marketplace.listingContent')}</label>
                <select
                  value={bundleFilter}
                  onChange={(e) => setBundleFilter(e.target.value as 'all' | 'single' | 'bundle')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="all">{t('marketplace.allContent')}</option>
                  <option value="single">{t('marketplace.singleOnly')}</option>
                  <option value="bundle">{t('marketplace.bundleOnly')}</option>
                </select>
              </div> 
              */}

              {/* Removed Apply Filters button as it is now automatic */}
              {/* <div className="mt-4">
                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  {t('marketplace.applyFilters')}
                </button>
              </div> */}
            </>
          )}
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-8 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Results Section */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('home.loading')}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Results Count */}
          <div className="mb-4 text-sm text-gray-600">
            {t('marketplace.showingListings', { count: listings.length })}
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6">
            {listings.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div className="text-6xl mb-4">🎴</div>
                <p className="text-gray-500 text-lg">{t('marketplace.noListings')}</p>
                <p className="text-gray-400 mt-2">
                  {t('marketplace.tryAdjusting')}
                </p>
              </div>
            ) : (
              listings.map((listing) => {
                const isBundle = Boolean(listing.isBundle);
                const bundleItems = isBundle ? parseBundleItems(listing.bundleItems) : [];
                const sellerImages = parseImages(listing.imageUrls);
                const primaryImage = isBundle
                  ? sellerImages[0] || undefined // bundles should show user-uploaded photo
                  : listing.card?.imageUrl || sellerImages[0];
                const cardName = isBundle
                  ? listing.bundleName || `${bundleItems.length || listing.quantity} ${t('marketplace.bundle')}`
                  : listing.card?.name || listing.customCardName || 'Unknown Card';

                return (
                  <Link
                    key={listing.id}
                    to={`/marketplace/listing/${listing.id}`}
                    className="group"
                  >
                    <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-500 hover:shadow-xl transition-all duration-200 h-full flex flex-col">
                      {/* Card Image */}
                      <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                        {primaryImage ? (
                          <img
                            src={primaryImage}
                            alt={cardName}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-6xl mb-2">🎴</div>
                              <p className="text-gray-400 text-sm">{t('home.noImage')}</p>
                            </div>
                          </div>
                        )}

                        {/* Listing Type Badge */}
                        <div className="absolute top-2 left-2">
                          <span
                            className={`px-2 py-1 text-xs font-bold rounded shadow-lg ${listing.listingType === 'WTB'
                              ? 'bg-green-600 text-white'
                              : 'bg-blue-600 text-white'
                              }`}
                          >
                            {listing.listingType || 'WTS'}
                          </span>
                        </div>

                        {/* Bundle Badge */}
                        {isBundle && (
                          <div className="absolute top-2 right-2">
                            <span className="px-2 py-1 text-xs font-bold rounded shadow-lg bg-indigo-600 text-white">
                              {t('marketplace.bundle')}
                            </span>
                          </div>
                        )}

                        {/* Rarity Badge */}
                        {!isBundle && listing.card?.rarity && (
                          <div className="absolute top-2 right-2">
                            <span
                              className={`px-2 py-1 text-xs font-bold rounded shadow-lg ${getRarityColor(listing.card.rarity)}`}
                            >
                              {getRarityAbbreviation(listing.card.rarity)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Card Info */}
                      <div className="p-2 md:p-4 flex-1 flex flex-col">
                        <h3 className="font-bold text-sm md:text-lg mb-1 truncate group-hover:text-blue-600 transition">
                          {cardName}
                        </h3>

                        {isBundle ? (
                          <div className="mb-2 space-y-1 text-sm text-gray-700">
                            {bundleItems.length > 0 ? (
                              <>
                                {bundleItems.slice(0, 3).map((item, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    <span className="text-gray-500">•</span>
                                    <span className="truncate">
                                      {item.quantity ? `${item.quantity}x ` : ''}{item.name}
                                      {item.notes ? ` (${item.notes})` : ''}
                                    </span>
                                  </div>
                                ))}
                                {bundleItems.length > 3 && (
                                  <div className="text-xs text-gray-500">+ {bundleItems.length - 3} more</div>
                                )}
                              </>
                            ) : (
                              <div className="text-gray-500 text-sm">Bundle contents listed in detail view</div>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-between mb-2">
                            {listing.card?.cardNumber && (
                              <span className="text-xs text-gray-500">
                                #{listing.card.cardNumber}
                              </span>
                            )}
                            {listing.card?.type && (
                              <span className={`text-xs px-2 py-1 rounded font-medium ${getTypeBackground(listing.card.type)}`}>
                                {listing.card.type}
                              </span>
                            )}
                          </div>
                        )}



                        <div className="mt-auto space-y-2">
                          <div className="flex items-start justify-between gap-2 text-xs text-gray-500">
                            <div className="min-w-0">
                              <span>{listing.listingType === 'WTB' ? t('marketplace.buyer') : t('marketplace.seller')}: </span>
                              <Link to={`/users/${listing.seller.id}/reviews`} className="text-blue-700 font-semibold hover:underline">
                                {listing.seller.displayName}
                              </Link>
                              {userRatings[listing.seller.id] && (
                                <div className="mt-1 flex items-center gap-1 text-gray-600">
                                  {renderStars(Math.round(userRatings[listing.seller.id].averageRating))}
                                  <span className="font-semibold text-gray-800">
                                    {userRatings[listing.seller.id].averageRating.toFixed(1)}
                                  </span>
                                  <span className="text-gray-500">({userRatings[listing.seller.id].totalReviews})</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                            <div>
                              <span className={`text-base md:text-2xl font-bold ${listing.listingType === 'WTB' ? 'text-green-600' : 'text-blue-600'}`}>
                                {listing.price != null
                                  ? `${listing.price.toLocaleString('en-US', { maximumFractionDigits: 0 })} THB`
                                  : t('marketplaceListing.offerPrice')}
                              </span>
                            </div>
                            <span className="text-sm text-gray-500">
                              {isBundle ? t('marketplace.bundle') : t('home.qty')}: {listing.quantity}
                            </span>
                          </div>

                          <button className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition font-medium">
                            {t('marketplace.viewDetails')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NewMarketplacePage;
